/**
 * Organization Knowledge Base Service - Frontend API client for organization KB operations
 * (Formerly Admin Library Service)
 */

import type {
  AdminLibraryItem,
  AdminLibraryTag,
  AdminLibraryStatus,
  CreateLibraryItemParams,
  UpdateLibraryItemParams,
  ListLibraryItemsParams,
  ListLibraryItemsResponse,
  CreateTagParams,
  SearchTagsParams,
} from '../types/adminLibrary';
import { supabase } from '@foundation/utils/supabaseClient';
import { getActiveWorkspaceId } from '../../../header/activeWorkspace';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Helper to get auth headers
const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const userId = session?.user?.id;

  const orgId = getActiveWorkspaceId() ?? '';

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-user-id': userId || '',
    'x-org-id': orgId,
  };
};

// =============================================
// Library Item Operations
// =============================================

export const createLibraryItem = async (
  params: CreateLibraryItemParams
): Promise<AdminLibraryItem> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create library item');
  }

  return response.json();
};

export const listLibraryItems = async (
  params: ListLibraryItemsParams = {}
): Promise<ListLibraryItemsResponse> => {
  const headers = await getAuthHeaders();

  // Build query string
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.tags && params.tags.length > 0) {
    params.tags.forEach(tag => queryParams.append('tags', tag));
  }
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const response = await fetch(
    `${API_BASE_URL}/api/revisr/org-kb?${queryParams.toString()}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list library items');
  }

  return response.json();
};

export const getLibraryItem = async (id: string): Promise<AdminLibraryItem> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/${id}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get library item');
  }

  return response.json();
};

export const updateLibraryItem = async (
  id: string,
  params: UpdateLibraryItemParams
): Promise<AdminLibraryItem> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update library item');
  }

  return response.json();
};

export const deleteLibraryItem = async (id: string): Promise<void> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete library item');
  }
};

export const publishLibraryItem = async (id: string): Promise<AdminLibraryItem> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/${id}/publish`, {
    method: 'PATCH',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to publish library item');
  }

  return response.json();
};

export const unpublishLibraryItem = async (id: string): Promise<AdminLibraryItem> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/${id}/unpublish`, {
    method: 'PATCH',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unpublish library item');
  }

  return response.json();
};

// =============================================
// File Upload Operation
// =============================================

export const uploadPolicyFile = async (
  file: File,
  title: string,
  description: string,
  tagIds: string[],
  status: 'draft' | 'published' = 'draft',
  visibility: boolean = false,
  sharedWith: Array<{ org_id?: string; user_id?: string }> = [],
  onProgress?: (progress: number) => void
): Promise<AdminLibraryItem> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', title);
  formData.append('description', description);
  formData.append('status', status);
  formData.append('tag_ids', JSON.stringify(tagIds));
  formData.append('visibility', String(visibility));
  formData.append('shared_with', JSON.stringify(sharedWith));

  // Get auth info from Supabase session
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const userId = session?.user?.id;

  const orgId = getActiveWorkspaceId() ?? '';

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const progress = (e.loaded / e.total) * 100;
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);
          resolve(result);
        } catch (error) {
          reject(new Error('Failed to parse response'));
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.error || error.message || 'Upload failed'));
        } catch {
          reject(new Error('Upload failed'));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('POST', `${API_BASE_URL}/api/revisr/org-kb/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('x-user-id', userId || '');
    xhr.setRequestHeader('x-org-id', orgId);

    xhr.send(formData);
  });
};

// =============================================
// Tag Operations
// =============================================

export const listTags = async (): Promise<AdminLibraryTag[]> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/tags`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list tags');
  }

  return response.json();
};

export const searchTags = async (params: SearchTagsParams): Promise<AdminLibraryTag[]> => {
  const headers = await getAuthHeaders();
  const queryParams = new URLSearchParams();
  queryParams.append('q', params.q);
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const response = await fetch(
    `${API_BASE_URL}/api/revisr/org-kb/tags/search?${queryParams.toString()}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to search tags');
  }

  return response.json();
};

export const createTag = async (params: CreateTagParams): Promise<AdminLibraryTag> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/tags`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create tag');
  }

  return response.json();
};

export const deleteTag = async (id: string): Promise<void> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/tags/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete tag');
  }
};

export type DocumentPreviewUrls = {
  fileUrl?: string;
  markdownUrl?: string;
  textContent?: string;
  fileType: string;
  filename: string;
};

export const getDocumentPreviewUrls = async (id: string): Promise<DocumentPreviewUrls> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-kb/${id}/preview-url`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get preview URLs');
  }

  return response.json();
};

// Export as default object
export const adminLibraryService = {
  createLibraryItem,
  listLibraryItems,
  getLibraryItem,
  updateLibraryItem,
  deleteLibraryItem,
  publishLibraryItem,
  unpublishLibraryItem,
  uploadPolicyFile, // Updated to include visibility and sharedWith parameters
  listTags,
  searchTags,
  createTag,
  deleteTag,
  getDocumentPreviewUrls,
};
